import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./styles.module.css";
import { Button } from "src/@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "src/@/components/ui/card";
import { Loader } from "src/components/designSystem/atoms/Loader";

const InstagramConnect: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [instagramAccount, setInstagramAccount] = useState<any>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Instagram OAuth URL - using exact URL to avoid any redirect_uri issues
  const instagramConnectUrl = "https://www.instagram.com/oauth/authorize?enable_fb_login=0&force_authentication=1&client_id=1293633251914608&redirect_uri=https://user.heyseva.com/instagram-auth&response_type=code&scope=instagram_business_basic,instagram_business_manage_messages,instagram_business_manage_comments,instagram_business_manage_insights";

  useEffect(() => {
    const fetchInstagramAccount = async () => {
      setLoading(true);
      setConnectionError(null);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem("token");
        const res = await fetch(`${backendUrl}/instagram-business/user`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = await res.json();
        if (res.ok && data.data && data.data.length > 0) {
          setInstagramAccount(data.data[0]);
        } else {
          setInstagramAccount(null);
          if (data?.error) setConnectionError(data.error);
        }
      } catch (error) {
        setInstagramAccount(null);
        setConnectionError(`Fetch error: ${(error as Error).message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchInstagramAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDisconnect = async () => {
    if (!instagramAccount) return;
    try {
      setLoading(true);
      setConnectionError(null);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      const res = await fetch(`${backendUrl}/instagram-business/disconnect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ instagramId: instagramAccount.id }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setInstagramAccount(null);
        toast.success("Instagram account disconnected successfully");
      } else {
        setConnectionError(data?.error || "Failed to disconnect Instagram account");
        toast.error(data?.error || "Failed to disconnect Instagram account");
      }
    } catch (error) {
      setConnectionError(`Disconnect error: ${(error as Error).message}`);
      toast.error("Failed to disconnect Instagram account");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardHeader>
          <CardTitle>Instagram Connection</CardTitle>
          <CardDescription>
            Connect your Instagram Business account to enable messaging and content management
          </CardDescription>
        </CardHeader>
        <CardContent>
          {instagramAccount ? (
            <div className={styles.accountInfo}>
              <h3>Connected Account</h3>
              <div className={styles.accountDetails}>
                <p><strong>Username:</strong> {instagramAccount.username}</p>
                <p><strong>Account Type:</strong> {instagramAccount.accountType || instagramAccount.account_type}</p>
                <p><strong>Media Count:</strong> {instagramAccount.mediaCount || instagramAccount.media_count || 'N/A'}</p>
                <p><strong>Connected Since:</strong> {instagramAccount.connectedAt ? new Date(instagramAccount.connectedAt).toLocaleString() : ''}</p>
                <p><strong>Token Expires:</strong> {instagramAccount.expiresAt ? new Date(instagramAccount.expiresAt).toLocaleString() : ''}</p>
              </div>
            </div>
          ) : (
            <div className={styles.connectPrompt}>
              <p>
                You need to connect your Instagram Business account to use all features.
                Click the button below to authenticate with Instagram.
              </p>
              <p className={styles.note}>
                <strong>Note:</strong> This requires an Instagram Business or Creator account
                that is connected to a Facebook Page.
              </p>
              {connectionError && (
                <div className={styles.errorMessage}>
                  <p><strong>Error:</strong> {connectionError}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className={styles.footer}>
          {instagramAccount ? (
            <Button 
              variant="destructive" 
              onClick={handleDisconnect}
              disabled={loading}
            >
              Disconnect Instagram
            </Button>
          ) : (
            <Button 
              onClick={() => window.location.href = instagramConnectUrl}
              disabled={loading}
            >
              Connect Instagram
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => navigate('/instagram-dashboard')}
            disabled={loading}
          >
            Back to Dashboard
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default InstagramConnect; 